import {sendEvent} from '@github-ui/hydro-analytics'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {SidebarListItem} from '@github-ui/pacer/SidebarListItem'
import {SidebarSection} from '@github-ui/pacer/SidebarNavigation'
import {useLocation} from '@github-ui/react-router'
import {AlertIcon, CheckIcon, DotFillIcon, StopIcon} from '@primer/octicons-react'
import {FormControl, RelativeTime, TextInput} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {type FormEvent, Fragment, useCallback, useId, useRef, useState} from 'react'
import {ConsecutiveCAPI401sProvider} from '../contexts/ConsecutiveCAPI401sContext'
import {TaskActionProviders} from './task-list/TaskActionProviders'
import {ArchiveErrorBanner} from './ArchiveErrorBanner'
import {useSSOChangeDetection} from '../hooks/use-sso-change-detection'
import {useTasks} from '../hooks/useTasks'
import {getReadableSessionState} from '../types/session'
import type {OptimisticTask, UITask} from '../types/task'
import {resolveAgentProfileForTask} from '../agents/resolve-agent-profile'
import {generateTaskPathForRepo} from '../utils/generate-task-path'
import {getTaskTitle} from '../utils/task-helpers'
import {InProgressSpinner} from './StatusIcon'
import {TaskActions} from './TaskActions'
import {TaskLeadingVisual} from './TaskLeadingVisual'
import {useIntentDetection} from '../hooks/useIntentDetection'
import {getPRStatusIcon} from './PRStatusIcon'
import {useRenameTask} from '../hooks/use-rename-task'

const PAGE_SIZE = 5

const TASK_BUCKET_LABELS = ['Today', 'This week', 'Older'] as const

export type TaskBucketLabel = (typeof TASK_BUCKET_LABELS)[number]

export interface TaskBucket<T> {
  label: TaskBucketLabel
  tasks: T[]
}

export function getTaskBucketTestId(label: TaskBucketLabel): string {
  return `task-bucket-${label.toLowerCase().replace(/\s+/g, '-')}`
}

function getStartOfDay(date: Date): Date {
  const startOfDay = new Date(date)
  startOfDay.setHours(0, 0, 0, 0)
  return startOfDay
}

function getStartOfWeek(date: Date): Date {
  const startOfWeek = getStartOfDay(date)
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay())
  return startOfWeek
}

function getTaskBucketLabel(updatedAt: string, now: Date): TaskBucketLabel {
  const updatedAtDate = new Date(updatedAt)

  if (Number.isNaN(updatedAtDate.getTime())) {
    return 'Older'
  }

  const startOfToday = getStartOfDay(now)
  if (updatedAtDate >= startOfToday) {
    return 'Today'
  }

  const startOfCurrentWeek = getStartOfWeek(now)
  if (updatedAtDate >= startOfCurrentWeek) {
    return 'This week'
  }

  return 'Older'
}

export function sortTasksByUpdatedAtDesc<T extends Pick<UITask, 'latest_session_updated_at'>>(tasks: T[]): T[] {
  return [...tasks].sort((leftTask, rightTask) => {
    return (
      new Date(rightTask.latest_session_updated_at).getTime() - new Date(leftTask.latest_session_updated_at).getTime()
    )
  })
}

export function groupTasksByUpdatedAt<T extends Pick<UITask, 'latest_session_updated_at'>>(
  tasks: T[],
  now: Date = new Date(),
): Array<TaskBucket<T>> {
  const buckets = new Map<TaskBucketLabel, T[]>(TASK_BUCKET_LABELS.map(label => [label, []]))

  for (const task of sortTasksByUpdatedAtDesc(tasks)) {
    buckets.get(getTaskBucketLabel(task.latest_session_updated_at, now))?.push(task)
  }

  return TASK_BUCKET_LABELS.map(label => ({label, tasks: buckets.get(label) ?? []})).filter(
    bucket => bucket.tasks.length > 0,
  )
}

export function TaskBucketHeader({label}: {label: TaskBucketLabel}) {
  return (
    <li data-testid={getTaskBucketTestId(label)}>
      <div className="m-0 px-2 py-2 color-fg-muted text-small text-semibold">{label}</div>
    </li>
  )
}

function getPRStatusLabel(task: UITask): string | undefined {
  const {pull} = task
  if (!pull) return undefined
  if (pull.state === 'merged') return 'Merged'
  if (pull.state === 'closed') return 'Closed'
  if (pull.state === 'open' && pull.reviewable_state === 'draft') return 'Draft'
  if (pull.inMergeQueue) return 'Queued to merge'
  return undefined
}

export function getTaskSecondaryText(task: UITask): React.ReactNode {
  if (task.isOptimistic) {
    return 'Initializing...'
  }

  const agentProfile = resolveAgentProfileForTask(task)

  if (agentProfile.interactiveSession && agentProfile.secondaryLabel) {
    const sessionLabel = task.remote_steerable
      ? `Remote ${agentProfile.secondaryLabel} session`
      : `${agentProfile.secondaryLabel} session (read only)`
    return (
      <>
        {sessionLabel}
        {' · '}
        <RelativeTime format="micro" datetime={task.latest_session_updated_at} />
      </>
    )
  }

  const isActiveSession = task.latest_session_state === 'in_progress' || task.latest_session_state === 'queued'
  const statusText = isActiveSession
    ? getReadableSessionState(task.latest_session_state, {hasPull: !!task.pull})
    : getPRStatusLabel(task) || getReadableSessionState(task.latest_session_state, {hasPull: !!task.pull})

  return (
    <>
      {statusText}
      {task.pull?.number ? ` · #${task.pull.number}` : ''}
      {' · '}
      <RelativeTime format="micro" datetime={task.latest_session_updated_at} />
    </>
  )
}

export function TasksListWrapper({
  viewAllLink,
  showTitle,
  trailingAction,
  onTaskClick,
}: {
  viewAllLink?: React.ReactNode
  showTitle?: boolean
  trailingAction?: React.ReactNode
  onTaskClick?: () => void
}) {
  return (
    <ConsecutiveCAPI401sProvider>
      <TaskActionProviders>
        <ArchiveErrorBanner />
        <TasksList
          viewAllLink={viewAllLink}
          showTitle={showTitle}
          trailingAction={trailingAction}
          onTaskClick={onTaskClick}
        />
      </TaskActionProviders>
    </ConsecutiveCAPI401sProvider>
  )
}

function TasksList({
  viewAllLink,
  showTitle,
  trailingAction,
  onTaskClick,
}: {
  viewAllLink?: React.ReactNode
  showTitle?: boolean
  trailingAction?: React.ReactNode
  onTaskClick?: () => void
}) {
  const location = useLocation()

  const {tasks, isLoading, dataUpdatedAt} = useTasks({
    taskFilter: 'active',
    pageSize: PAGE_SIZE,
    includeCounts: true,
  })
  useSSOChangeDetection()

  const visibleTasks = sortTasksByUpdatedAtDesc(tasks).slice(0, PAGE_SIZE)
  const groupedVisibleTasks = groupTasksByUpdatedAt(visibleTasks)
  const isEmpty = visibleTasks.length === 0
  const {isUsingIntentDetection} = useIntentDetection()

  return (
    <>
      <SidebarSection
        title={showTitle ? 'Agent sessions' : undefined}
        isEmpty={isEmpty}
        isLoading={isLoading}
        trailingAction={trailingAction}
      >
        {groupedVisibleTasks.map(bucket => (
          <Fragment key={bucket.label}>
            {groupedVisibleTasks.length > 1 && <TaskBucketHeader label={bucket.label} />}
            {bucket.tasks.map(task => {
              const href = generateTaskPathForRepo({
                repositoryNwo: task.nwo,
                pullRequestNumber: task.pull?.number ?? 0,
                sessionId: task.latest_session_id,
                taskId: task.id,
                isUsingIntentDetection,
              })
              const isSelected = location.pathname === href.split('?')[0]

              const nwo = task.nwo
              const [owner, repo] = nwo ? nwo.split('/') : [undefined, undefined]

              return (
                <TaskListEntry
                  key={task.clientGeneratedId ?? task.id ?? task.resource_id}
                  task={task}
                  href={href}
                  isSelected={isSelected}
                  reloadDocument={!isUsingIntentDetection && !!task.nwo}
                  secondaryText={getTaskSecondaryText(task)}
                  onTaskClick={onTaskClick}
                  repoOwner={owner}
                  repoName={repo}
                  dataUpdatedAt={dataUpdatedAt}
                  currentUserIsOwner
                />
              )
            })}
          </Fragment>
        ))}
      </SidebarSection>
      {!isEmpty && viewAllLink}
    </>
  )
}

export interface TaskListEntryProps {
  task: UITask
  href: string
  isSelected: boolean
  secondaryText: string | React.ReactNode
  reloadDocument: boolean
  onTaskClick?: () => void
  repoOwner?: string
  repoName?: string
  currentUserIsOwner?: boolean
  preferPullTitle?: boolean
  formatAsReview?: boolean
  dataUpdatedAt?: number
}

export function TaskListEntry({
  task,
  href,
  isSelected,
  secondaryText,
  reloadDocument,
  onTaskClick,
  repoOwner,
  repoName,
  currentUserIsOwner,
  preferPullTitle,
  formatAsReview,
  dataUpdatedAt,
}: TaskListEntryProps) {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [isRenaming, setIsRenaming] = useState(false)

  const sessionState = task.latest_session_state
  const prState = task.pull?.state
  const hasPullRequest = !!task.pull
  const isOptimistic = !!task.isOptimistic
  const prInMergeQueue = task.pull?.inMergeQueue
  const isArchived = !!task.archived_at
  const agentProfile = resolveAgentProfileForTask(task)
  const LeadingIcon = agentProfile.slots.LeadingIcon
  const TrailingVisual = agentProfile.slots.TrailingVisual
  const sessionFiltersEnabled = isFeatureEnabled('copilot_mission_control_session_filters')
  const EffectiveLeadingIcon = sessionFiltersEnabled ? null : LeadingIcon
  const isDraftPullRequest = prState === 'open' && task.pull?.reviewable_state === 'draft'
  const isActiveTask = sessionState === 'queued' || sessionState === 'in_progress'

  const taskTitle = getTaskTitle(task, task.pull?.title, {preferPullTitle, formatAsReview})

  const handleStartRename = useCallback(() => {
    setIsRenaming(true)
    sendEvent('agent_sessions.task_actions', {
      target: 'RENAME_TASK_START',
      task_id: task.id,
    })
  }, [task.id])

  const handleKebabOpen = useCallback(() => {
    sendEvent('dotcom_chat.activate', {
      target: 'TASK_CONTEXT_MENU',
      mode: 'immersive',
    })
  }, [])

  const trailingAction = (
    <TaskActions
      ariaLabel="Manage session"
      anchorRef={anchorRef}
      onOpen={handleKebabOpen}
      state={task.latest_session_state}
      pull={task.pull}
      taskId={task.id}
      repoOwner={repoOwner}
      repoName={repoName}
      currentUserIsOwner={currentUserIsOwner}
      archivedAt={task.archived_at}
      eventType={task.event_type}
      eventIdentifiers={task.event_identifiers}
      interactiveSession={agentProfile.interactiveSession}
      agentSlug={agentProfile.slug}
      hideCopy={agentProfile.privateSessions}
      hideShare={!agentProfile.privateSessions}
      onRename={handleStartRename}
      sharingStatus={task.sharing_status}
    />
  )

  const effectivePrStatus = isDraftPullRequest ? 'draft' : prState

  const leadingVisual = useCallback(() => {
    if (EffectiveLeadingIcon) {
      return (
        <span style={{color: 'var(--fgColor-muted)'}}>
          <EffectiveLeadingIcon
            state={sessionState}
            prState={prState}
            prInMergeQueue={prInMergeQueue}
            isArchived={isArchived}
            isOptimistic={isOptimistic}
            staleAt={task.stale_at}
            dataUpdatedAt={dataUpdatedAt}
            sharingStatus={task.sharing_status}
          />
        </span>
      )
    }

    if (prInMergeQueue || isArchived) {
      return (
        <TaskLeadingVisual
          isOptimistic={isOptimistic}
          prState={prState}
          prInMergeQueue={prInMergeQueue}
          sessionState={sessionState}
          isArchived={isArchived}
        />
      )
    }

    if (isOptimistic || isActiveTask) {
      return (
        <span data-testid="task-list-entry-active-icon" style={{display: 'inline-flex', marginTop: '3px'}}>
          <InProgressSpinner />
        </span>
      )
    }

    if (hasPullRequest) {
      return getPRStatusIcon(effectivePrStatus || 'open')
    }

    // When session_filters FF is on, treat `idle` (CLI-only state) as completed visually.
    if (sessionState === 'completed' || (sessionFiltersEnabled && sessionState === 'idle')) {
      return <CheckIcon className="fgColor-success" data-testid="task-list-entry-completed-icon" />
    }

    if (sessionState === 'waiting_for_user') {
      return <AlertIcon className="fgColor-attention" data-testid="task-list-entry-waiting-for-user-icon" />
    }

    if (sessionState === 'failed' || sessionState === 'timed_out' || sessionState === 'cancelled') {
      return <StopIcon className="fgColor-danger" data-testid="task-list-entry-error-icon" />
    }

    return <DotFillIcon className="fgColor-muted" data-testid="task-list-entry-inactive-icon" />
  }, [
    EffectiveLeadingIcon,
    prInMergeQueue,
    isArchived,
    isOptimistic,
    prState,
    sessionState,
    isActiveTask,
    hasPullRequest,
    effectivePrStatus,
    sessionFiltersEnabled,
    task.stale_at,
    task.sharing_status,
    dataUpdatedAt,
  ])

  return (
    <>
      <SidebarListItem
        href={href}
        text={taskTitle}
        leadingVisual={leadingVisual}
        isSelected={isSelected}
        trailingAction={trailingAction}
        crossfadeTrailing
        trailingVisual={
          TrailingVisual ? (
            <TrailingVisual
              state={sessionState}
              staleAt={task.stale_at}
              dataUpdatedAt={dataUpdatedAt}
              sharingStatus={task.sharing_status}
            />
          ) : undefined
        }
        secondaryText={secondaryText}
        reloadDocument={reloadDocument}
        onClick={() => {
          onTaskClick?.()
          sendEvent('agent_sessions.task_view', {
            target: 'VIEW_TASK_FROM_MISSION_CONTROL',
            task_id: task.resource_global_id,
          })
        }}
      />
      {isRenaming && (
        <RenameTaskDialog
          currentName={task.name || taskTitle}
          taskId={task.id}
          repoOwner={repoOwner}
          repoName={repoName}
          onClose={() => setIsRenaming(false)}
          returnFocusRef={anchorRef}
        />
      )}
    </>
  )
}

interface RenameTaskDialogProps {
  currentName: string
  taskId?: string
  repoOwner?: string
  repoName?: string
  onClose: () => void
  returnFocusRef: React.RefObject<HTMLElement | null>
}

function RenameTaskDialog({currentName, taskId, repoOwner, repoName, onClose, returnFocusRef}: RenameTaskDialogProps) {
  const [invalid, setInvalid] = useState<'required' | 'max_length' | undefined>(undefined)
  const inputRef = useRef<HTMLInputElement>(null)
  const formId = useId()
  const dialogId = useId()
  const {mutateAsync: renameMutateAsync, isPending: isRenamePending} = useRenameTask({
    owner: repoOwner,
    repo: repoName,
    taskId,
  })

  const submit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault()
      const value = inputRef.current?.value?.trim() ?? ''

      if (!value) {
        setInvalid('required')
        inputRef.current?.focus()
      } else if (value.length > 100) {
        setInvalid('max_length')
        inputRef.current?.focus()
      } else {
        setInvalid(undefined)
        try {
          await renameMutateAsync({name: value})
          sendEvent('agent_sessions.task_actions', {
            target: 'RENAME_TASK_SAVE',
            task_id: taskId,
          })
          onClose()
        } catch {
          // Keep dialog open on failure
        }
      }
    },
    [renameMutateAsync, taskId, onClose],
  )

  const handleClose = useCallback(() => {
    sendEvent('agent_sessions.task_actions', {
      target: 'RENAME_TASK_CANCEL',
      task_id: taskId,
    })
    onClose()
  }, [taskId, onClose])

  return (
    <Dialog
      title={<span id={dialogId}>Rename task</span>}
      onClose={handleClose}
      width="large"
      footerButtons={[
        {
          content: 'Cancel',
          onClick: handleClose,
        },
        {
          content: 'Update',
          buttonType: 'primary',
          type: 'submit',
          form: formId,
          disabled: isRenamePending,
        },
      ]}
      initialFocusRef={inputRef}
      returnFocusRef={returnFocusRef}
    >
      <form id={formId} onSubmit={submit}>
        <FormControl required>
          <FormControl.Label visuallyHidden>Rename task</FormControl.Label>
          <TextInput
            aria-labelledby={dialogId}
            aria-describedby={invalid ? 'error-message' : undefined}
            aria-invalid={invalid ? 'true' : undefined}
            ref={inputRef}
            defaultValue={currentName}
            block
            disabled={isRenamePending}
          />
          {invalid && (
            <FormControl.Validation id="error-message" variant="error">
              {invalid === 'required' ? 'Enter a name' : 'Name cannot exceed 100 characters'}
            </FormControl.Validation>
          )}
        </FormControl>
      </form>
    </Dialog>
  )
}

export function isOpenTask(task: UITask): boolean {
  return !task.pull || (task.pull.state !== 'merged' && task.pull.state !== 'closed')
}

export function isOptimisticTask(task: UITask): task is OptimisticTask {
  return !!task.isOptimistic
}
